/**
 * Sidebar collapse context.
 *
 * Manages the sidebar collapsed/expanded state across the app.
 * Defaults to expanded (w-64 full nav) on all routes.
 * State persisted in localStorage.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

interface SidebarContextValue {
    isCollapsed: boolean;
    toggleSidebar: () => void;
    setCollapsed: (val: boolean) => void;
}

const SidebarContext = createContext<SidebarContextValue>({
    isCollapsed: false,
    toggleSidebar: () => { },
    setCollapsed: () => { },
});

const STORAGE_KEY = 'sidebar-collapsed';

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Default to expanded; respect user preference from localStorage
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored !== null) return stored === 'true';
        return false;
    });

    const toggleSidebar = useCallback(() => {
        setIsCollapsed(prev => {
            const next = !prev;
            localStorage.setItem(STORAGE_KEY, String(next));
            return next;
        });
    }, []);

    const setCollapsed = useCallback((val: boolean) => {
        setIsCollapsed(val);
        localStorage.setItem(STORAGE_KEY, String(val));
    }, []);

    return (
        <SidebarContext.Provider value={{ isCollapsed, toggleSidebar, setCollapsed }}>
            {children}
        </SidebarContext.Provider>
    );
};

export const useSidebarCollapse = () => useContext(SidebarContext);
